//es-lint-disable-next-line
import { ClientResponse } from "@commercetools/platform-sdk";
import { CT_PRODUCT_LIMIT } from "@Components/products-listing/constants";
import { ITooltipData } from "@Types/Contentful";
import { callCTP } from "@wff/libs/commerce-tools";

interface HomePropsTypes {
  pageModel: any;
}

// Get data from Commercetools for the listed items
export const getCtData = async (sku: string) => {
  try {
    const res = await callCTP
      .products()
      .get({
        queryArgs: {
          localeProjection: "en-US", // do not change, all data is handled from en-US projection
          where: `masterData(current(masterVariant(sku="${sku}") or variants(sku="${sku}")))`,
          expand: ["productType"],
        },
      })
      .execute();
    return res?.body.results[0];
  } catch (e) {
    return null;
  }
};

/**
 * Fix: CE:357
 * Get all products from CT in recursive manner
 * @param offset
 * @returns
 */
export const getCtAllProducts = async (
  offset = 0
): Promise<ClientResponse | null> => {
  try {
    const response = await callCTP
      .products()
      .get({
        queryArgs: {
          limit: CT_PRODUCT_LIMIT,
          offset,
          where: "masterData(published=true)",
          localeProjection: "en-US", // do not change, all data is handled from en-US projection
          expand: ["productType"],
        },
      })
      .execute();

    const products = response.body?.results ? response.body.results : [];

    if (
      response?.body?.total &&
      offset + response?.body?.count < response?.body?.total
    ) {
      const nextProducts = await getCtAllProducts(offset + CT_PRODUCT_LIMIT);
      products.push(...(nextProducts?.body?.results ?? []));
    }

    return {
      ...response,
      body: {
        ...response.body,
        results: products,
      },
    };
  } catch (e) {
    return null;
  }
};

export const getCtDataBySlug = async (slug: string, lang: string) => {
  try {
    const where = `masterData(current(slug(${lang}="${slug}")))`;
    const localeProjection = "en-US";

    const getProduct = async (expand?: string[]) => {
      const queryArgs = {
        localeProjection,
        where,
        expand,
      };

      return await callCTP.products().get({ queryArgs }).execute();
    };

    const expand = ["productType"];
    const ctProduct = await getProduct(expand);
    const isProductSet =
      ctProduct?.body?.results?.[0]?.productType?.obj?.name?.toLowerCase?.() ===
      "product set";

    if (isProductSet) {
      const queryArgExpand = [
        ...expand,
        "masterData.current.masterVariant.attributes[*].value[*]",
      ];

      return await getProduct(queryArgExpand);
    }

    return ctProduct;
  } catch (e) {
    return null;
  }
};

export const updateSingleItems = async (
  itemsArr: any,
  tooltipData: ITooltipData
) => {
  const tempArr = [...itemsArr];
  for (let j = 0; j < tempArr.length; j++) {
    const sku = tempArr?.[j]?.ctproductSku;
    if (sku) {
      tempArr[j] = {
        ...tempArr[j],
        ctData: (await getCtData(sku)) ?? null,
        tooltipData: tooltipData,
      };
    }
  }
  return tempArr;
};

export const updatePageModel = (
  initialPageModel: HomePropsTypes["pageModel"]
) => {
  // find all instances with a CarouselProductList component to update the props adding a param
  // to pageModel

  const tempPM = { ...initialPageModel };
  const templates = tempPM?.pageContent?.templates?.items || [];
  const tooltipData = {
    title: tempPM?.pageContent?.["products.tooltip.title"]
      ? tempPM?.pageContent?.["products.tooltip.title"]
      : "",
    description: tempPM?.pageContent?.["products.tooltip.description"]
      ? tempPM?.pageContent?.["products.tooltip.description"]
      : "",
    example: tempPM?.pageContent?.["products.tooltip.example"]
      ? tempPM?.pageContent?.["products.tooltip.example"]
      : "",
  };
  if (
    templates?.find((el: any) => el?.content_type === "CarouselProductList")
  ) {
    // add the corresponding Commercetools info to each product.

    const updateAllItems = async (arr: any, tooltipData: ITooltipData) => {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i]?.content_type === "CarouselProductList") {
          const itemsArr = await updateSingleItems(
            [...(arr[i]?.carouselProductItems?.items ?? [])],
            tooltipData
          );

          arr[i] = {
            ...templates?.[i],
            carouselProductItems: {
              ...templates?.[i]?.carouselProductItems,
              items: [...itemsArr] ?? [],
            },
          };
        }
      }

      return arr;
    };

    return updateAllItems(templates, tooltipData);
  }
  return templates;
};
